import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { JobTypeVersion } from "../api/client";

/**
 * JobTypeForm — mirrors the DB model returned by the A5 CRUD API
 * (`GET /api/job-types/:id/forms`).
 */
export interface JobTypeForm {
  id: string;
  jobTypeId: string;
  name: string;
  classificationKey: string;
  isDefault: boolean;
  displayOrder: number;
  // 5c-1 per-page identity — authored on the Pages tab, returned by the
  // forms list/PATCH endpoints. Optional because older web fixtures omit them.
  allowsAttachments?: boolean;
  pageIdentityStrategy?: "none" | "positional" | "fingerprint" | "manual";
  // 5c-3 page-order enforcement — when true, pages must arrive in ordinal order.
  enforcePageOrder?: boolean;
  // 5c-4 auto-reorder — when true (and enforcePageOrder is true), misordered
  // pages are automatically resequenced to the expected ordinal order.
  autoReorderPages?: boolean;
  // E-053 — what happens when page assembly finds a doc incomplete:
  // park at classify for repair (default) or continue with the completeness
  // flag; rules read Completeness / Missing page types to stamp/hold/notify.
  incompleteHandling?: "park" | "continue";
  createdAt: string;
  updatedAt: string;
}

/**
 * Wave F 19c — POST /forms (and the clone route) carry the updated draft
 * version (containing the new form's content slice) so callers can absorb it
 * directly instead of a second createDraft round-trip.
 */
export type CreatedJobTypeForm = JobTypeForm & { draftVersion?: JobTypeVersion };

/**
 * useJobTypeForms — wraps the A5 `JobTypeForm` CRUD API.
 *
 * Pattern mirrors `apps/web/src/features/admin/hooks/useJobTypes.ts`:
 * same `useState`/`useEffect`/`useCallback` shape, same `api.*` fetch
 * wrapper, same return-object structure.
 *
 * Returns:
 *   forms   — ordered by displayOrder/name (API-guaranteed); [] while loading
 *   loading — true during the initial fetch
 *   create  — POST /job-types/:id/forms; returns the new form
 *   rename  — PATCH /job-types/:id/forms/:formId; returns the updated form
 *   remove  — DELETE /job-types/:id/forms/:formId; rejects with { error } on 409
 *   refetch — re-fetches the list
 */
// F-041 — cross-instance refetch coordination. Several components mount their
// OWN `useJobTypeForms` instance (FormConfigPage, FormSelector, AddFormDialog,
// ManageFormsDialog, FormPagesTab) with independent state and no shared cache,
// so a mutation in one (e.g. AddFormDialog.create) left the others stale — a
// new form didn't appear in the selector until a full page refresh. Each
// mounted instance registers its `load` here; any mutation notifies the others
// so they refetch. Keyed by jobTypeId so instances for different jobs stay
// isolated.
const formsRefetchers = new Map<string, Set<() => void>>();

function registerRefetcher(jobTypeId: string, fn: () => void): () => void {
  let set = formsRefetchers.get(jobTypeId);
  if (!set) {
    set = new Set();
    formsRefetchers.set(jobTypeId, set);
  }
  set.add(fn);
  return () => {
    const s = formsRefetchers.get(jobTypeId);
    s?.delete(fn);
    if (s && s.size === 0) formsRefetchers.delete(jobTypeId);
  };
}

/** Refetch every OTHER registered instance for this jobType (the caller has
 *  already refreshed its own state via `await load()`). */
function notifyFormsChanged(jobTypeId: string, except: () => void): void {
  formsRefetchers.get(jobTypeId)?.forEach((fn) => {
    if (fn !== except) fn();
  });
}

export function useJobTypeForms(jobTypeId: string) {
  const [forms, setForms] = useState<JobTypeForm[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!jobTypeId) return;
    setLoading(true);
    try {
      const data = await api.get<JobTypeForm[]>(`/job-types/${jobTypeId}/forms`);
      // Phase 1.5 WU-2 — defensive: stay an array even if a test mock or
      // misbehaving response returns null/object. Consumers like
      // JobTypeDetailPage call `.find()` on this list and would crash if a
      // bare object slipped through.
      setForms(Array.isArray(data) ? data : []);
    } catch {
      // Silently fall back to empty list — the FormStrip will render without
      // form chips if the fetch fails. Errors are not surfaced here because
      // failure is non-blocking (the fields tab still works without the strip).
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [jobTypeId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Register this instance so a mutation anywhere refetches it (F-041).
  useEffect(() => registerRefetcher(jobTypeId, load), [jobTypeId, load]);

  const create = useCallback(
    async (args: { name: string; classificationKey: string }): Promise<CreatedJobTypeForm> => {
      const form = await api.post<CreatedJobTypeForm>(`/job-types/${jobTypeId}/forms`, args);
      await load();
      notifyFormsChanged(jobTypeId, load);
      return form;
    },
    [jobTypeId, load],
  );

  const clone = useCallback(
    async (
      sourceId: string,
      args: { name: string; classificationKey: string },
    ): Promise<CreatedJobTypeForm> => {
      // POST /job-types/:id/forms/:sourceId/clone — server deep-copies the
      // source form's V3 slice + relational ZoneTemplate/FormOcrProfile rows,
      // and (Wave F 19c symmetry) returns draftVersion like blank-add.
      // Throws ApiError (409 duplicate_name | duplicate_classification_key).
      const form = await api.post<CreatedJobTypeForm>(
        `/job-types/${jobTypeId}/forms/${sourceId}/clone`,
        args,
      );
      await load();
      notifyFormsChanged(jobTypeId, load);
      return form;
    },
    [jobTypeId, load],
  );

  const rename = useCallback(
    async (
      formId: string,
      args: { name?: string; classificationKey?: string },
    ): Promise<JobTypeForm> => {
      const form = await api.patch<JobTypeForm>(
        `/job-types/${jobTypeId}/forms/${formId}`,
        args,
      );
      await load();
      notifyFormsChanged(jobTypeId, load);
      return form;
    },
    [jobTypeId, load],
  );

  const setDefault = useCallback(
    async (formId: string): Promise<JobTypeForm> => {
      // PATCH isDefault:true — the API transactionally clears the prior default,
      // guaranteeing exactly one default per job type.
      const form = await api.patch<JobTypeForm>(
        `/job-types/${jobTypeId}/forms/${formId}`,
        { isDefault: true },
      );
      await load();
      notifyFormsChanged(jobTypeId, load);
      return form;
    },
    [jobTypeId, load],
  );

  const reorder = useCallback(
    async (formId: string, direction: "up" | "down"): Promise<void> => {
      // Reorder = swap two forms' displayOrder. `forms` is API-ordered by
      // displayOrder, so the adjacent neighbor in this array is the swap target.
      const idx = forms.findIndex((f) => f.id === formId);
      if (idx === -1) return;
      const neighborIdx = direction === "up" ? idx - 1 : idx + 1;
      if (neighborIdx < 0 || neighborIdx >= forms.length) return; // no-op at the ends
      const current = forms[idx];
      const neighbor = forms[neighborIdx];
      if (!current || !neighbor) return;
      // Two PATCH calls (no transaction): reordering is a rare admin action and
      // displayOrder has no uniqueness constraint, so a partial failure is at
      // worst a cosmetic ordering glitch, self-healed by the next reorder.
      await api.patch(`/job-types/${jobTypeId}/forms/${current.id}`, {
        displayOrder: neighbor.displayOrder,
      });
      await api.patch(`/job-types/${jobTypeId}/forms/${neighbor.id}`, {
        displayOrder: current.displayOrder,
      });
      await load();
      notifyFormsChanged(jobTypeId, load);
    },
    [forms, jobTypeId, load],
  );

  const remove = useCallback(
    async (formId: string): Promise<void> => {
      // DELETE returns 204 on success, 409 on policy violation.
      // api.delete throws ApiError on non-2xx — callers catch 409 and
      // surface the `error` body (cannot_delete_default | form_has_fields).
      await api.delete(`/job-types/${jobTypeId}/forms/${formId}`);
      await load();
      notifyFormsChanged(jobTypeId, load);
    },
    [jobTypeId, load],
  );

  const refetch = useCallback(async () => {
    await load();
  }, [load]);

  return { forms, loading, create, clone, rename, setDefault, reorder, remove, refetch };
}
